<template>
  <div class="qv26__media">
    <div v-if="hasImages" ref="mobileSliderRef" class="qv26__mobile-slider">
      <div class="qv26__mobile-slider-wrapper">
        <div v-for="image in carouselImages" :key="image.src" class="qv26__mobile-slide">
          <ImageField
            class="qv26__image"
            :obj="{ url: image.src, alt: image.alt, width: 1200, height: 1500 }"
            width-param="width"
            sizes="100vw"
          />
        </div>
      </div>
    </div>

    <div v-if="showThumbs" class="qv26__thumbs-shell">
      <div ref="thumbsRef" class="qv26__thumbs" @scroll="updateThumbScrollState">
        <button
          v-for="(image, index) in carouselImages"
          :key="image.src"
          :class="['qv26__thumb', { 'is-active': index === activeIndex }]"
          :aria-label="`Show image ${index + 1}`"
          type="button"
          @click="activeIndex = index"
        >
          <ImageField
            class="qv26__thumb-image"
            :obj="{ url: image.src, alt: image.alt, width: 120, height: 120 }"
            width-param="width"
            sizes="120px"
          />
        </button>
      </div>

      <div
        v-if="thumbControlDirection"
        :class="['qv26__thumbs-fade', `qv26__thumbs-fade--${thumbControlDirection}`]"
        aria-hidden="true"
      ></div>

      <button
        v-if="thumbControlDirection"
        :class="['qv26__thumbs-control', `qv26__thumbs-control--${thumbControlDirection}`]"
        :aria-label="thumbControlDirection === 'down' ? 'Scroll thumbnail list down' : 'Scroll thumbnail list up'"
        type="button"
        @click="scrollThumbs"
      >
        <svg class="qv26__thumbs-control-icon">
          <use href="#green-chevron-down"></use>
        </svg>
      </button>
    </div>

    <div class="qv26__image-panel">
      <ImageField
        v-if="activeImage"
        :key="activeImage.src"
        class="qv26__image"
        :obj="{ url: activeImage.src, alt: activeImage.alt, width: 1200, height: 1500 }"
        width-param="width"
        sizes="(min-width: 1024px) 560px, 100vw"
      />
    </div>
  </div>
</template>

<script setup>
  import { computed, nextTick, onMounted, ref, toRefs, watch } from 'vue';
  import ImageField from '../global/image.vue';
  import useWindowResize from '../../utils/use-window-resize.js';

  const props = defineProps({
    product: {
      type: Object,
      default: () => ({}),
    },
    selectedColor: {
      type: String,
      default: '',
    },
    selectedTown: {
      type: String,
      default: null,
    },
    productForm: {
      type: Object,
      default: () => ({}),
    },
    firstOption: {
      type: String,
      default: '',
    },
  });

  const { product, selectedColor, selectedTown } = toRefs(props);
  const activeIndex = ref(0);
  const thumbsRef = ref(null);
  const mobileSliderRef = ref(null);
  const canScrollUp = ref(false);
  const canScrollDown = ref(false);
  const { windowWidth } = useWindowResize();

  const carouselImages = computed(() => {
    const { images, title } = product.value;
    return window.getProductImages(images, selectedColor.value, title, selectedTown.value);
  });

  const activeImage = computed(() => {
    return carouselImages.value[activeIndex.value] || carouselImages.value[0] || null;
  });

  const hasImages = computed(() => carouselImages.value.length > 0);
  const showThumbs = computed(() => carouselImages.value.length > 1);
  const isDesktop = computed(() => windowWidth.value >= 1024);
  const thumbControlDirection = computed(() => {
    if (!showThumbs.value) return null;
    if (canScrollDown.value) return 'down';
    if (canScrollUp.value) return 'up';
    return null;
  });

  const updateThumbScrollState = () => {
    const thumbsEl = thumbsRef.value;

    if (!thumbsEl) {
      canScrollUp.value = false;
      canScrollDown.value = false;
      return;
    }

    const scrollTop = thumbsEl.scrollTop;
    const maxScrollTop = thumbsEl.scrollHeight - thumbsEl.clientHeight;

    canScrollUp.value = scrollTop > 1;
    canScrollDown.value = maxScrollTop - scrollTop > 1;
  };

  const scrollThumbs = () => {
    const thumbsEl = thumbsRef.value;

    if (!thumbsEl) return;

    const direction = thumbControlDirection.value === 'up' ? -1 : 1;
    const scrollAmount = Math.max(thumbsEl.clientHeight - 120, 120);

    thumbsEl.scrollBy({
      top: direction * scrollAmount,
      behavior: 'smooth',
    });
  };

  const syncThumbRail = async () => {
    await nextTick();
    updateThumbScrollState();
  };

  const resetMobileSlider = async () => {
    await nextTick();

    if (!mobileSliderRef.value) return;

    mobileSliderRef.value.scrollTo({
      left: 0,
      behavior: 'auto',
    });
  };

  onMounted(() => {
    syncThumbRail();
  });

  watch(carouselImages, () => {
    activeIndex.value = 0;
    syncThumbRail();
    resetMobileSlider();
  });

  watch(activeIndex, async () => {
    await nextTick();
    const activeThumb = thumbsRef.value?.querySelector('.qv26__thumb.is-active');
    if (isDesktop.value) {
      activeThumb?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      window.requestAnimationFrame(updateThumbScrollState);
    }
  });
</script>
